import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BACKEND_URL, errorMessage } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const { request, isSignedIn } = useProfile();
  const audio = useRef(null);
  const latest = useRef(0);
  const play = useRef({ sessionId: null, need: 30, listened: 0, last: 0, sent: false });
  const [queue, setQueue] = useState([]);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState({ current: 0, duration: 0 });
  const [volume, setVolumeState] = useState(() => Number(localStorage.getItem("nw_volume") ?? 0.9));

  useEffect(() => { if (audio.current) audio.current.volume = volume; }, [volume]);
  useEffect(() => { if (!isSignedIn && audio.current) { audio.current.pause(); setTrack(null); } }, [isSignedIn]);

  const playTrack = useCallback(async (t, list) => {
    const el = audio.current;
    if (track?.id === t.id && el.getAttribute("src")) { el.paused ? el.play() : el.pause(); return; }
    if (list) setQueue(list);
    const reqId = ++latest.current;
    el.pause();
    setTrack(t); setLoading(true); setTime({ current: 0, duration: t.duration_seconds || 0 });
    try {
      const s = await request("post", `/tracks/${t.id}/stream-session`);
      if (reqId !== latest.current) return;
      play.current = { sessionId: s.session_id, need: s.qualify_after_seconds, listened: 0, last: 0, sent: false, retried: false };
      el.src = `${BACKEND_URL}${s.stream_url}`;
      await el.play();
    } catch (e) {
      if (reqId !== latest.current || e?.name === "AbortError") return;
      toast.error(errorMessage(e)); setLoading(false); el.removeAttribute("src"); setTrack(null);
    }
  }, [track, request]);

  // Stream tokens expire; on a stream error, fetch a fresh URL once and resume at the same spot.
  const onError = async () => {
    const el = audio.current, p = play.current;
    if (!el.getAttribute("src") || !track) return;
    if (p.retried) { setLoading(false); toast.error("Could not stream this track"); return; }
    p.retried = true;
    const at = el.currentTime;
    try {
      const s = await request("post", `/tracks/${track.id}/stream-session`);
      el.src = `${BACKEND_URL}${s.stream_url}&r=${Date.now()}`;
      el.currentTime = at; p.last = at;
      await el.play();
    } catch { setLoading(false); toast.error("Could not stream this track"); }
  };

  const step = useCallback((dir) => {
    const i = queue.findIndex((q) => q.id === track?.id);
    const next = queue[i + dir];
    if (next) playTrack(next);
  }, [queue, track, playTrack]);

  const onTimeUpdate = () => {
    const el = audio.current, p = play.current;
    const delta = el.currentTime - p.last;
    if (delta > 0 && delta < 1.5) p.listened += delta;
    p.last = el.currentTime;
    setTime({ current: el.currentTime, duration: el.duration || track?.duration_seconds || 0 });
    if (!p.sent && p.sessionId && p.listened >= p.need) {
      p.sent = true;
      request("post", `/plays/${p.sessionId}/qualify`, { listened_seconds: p.listened }).catch(() => {});
    }
  };

  const value = {
    track, playing, loading, time, volume, queue, playTrack,
    pause: () => audio.current?.pause(),
    toggle: () => { const el = audio.current; if (track) el.paused ? el.play() : el.pause(); },
    seek: (s) => { audio.current.currentTime = s; play.current.last = s; },
    setVolume: (v) => { setVolumeState(v); localStorage.setItem("nw_volume", v); },
    next: () => step(1), prev: () => step(-1),
    close: () => { audio.current.pause(); audio.current.removeAttribute("src"); setTrack(null); },
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio ref={audio} preload="metadata" data-testid="player-audio-element"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); step(1); }}
        onWaiting={() => setLoading(true)} onPlaying={() => { setLoading(false); play.current.retried = false; }} onCanPlay={() => setLoading(false)}
        onTimeUpdate={onTimeUpdate} onLoadedMetadata={onTimeUpdate}
        onError={onError} />
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
